// Pure streak derivations. No React, no network — every branch here is unit
// tested in streak.test.js because these functions decide what the user sees
// on a surface that renders before any of their data has loaded.

export const RECOVERY_VOTES_GOAL = 10;

/**
 * The three tiers, mirroring backend `computeUnlockedTiers`. Both unlock paths
 * are real: a consecutive-day streak OR a lifetime vote count, whichever the
 * user reaches first. Heavy-but-unstreaky voters still get the perks.
 */
export const TIERS = [
  {
    n: 1,
    days: 3,
    votes: 25,
    title: 'Expressive Identity',
    reward: 'Archetype badge on your profile and comments',
  },
  {
    n: 2,
    days: 7,
    votes: 100,
    title: 'Insider Access',
    reward: 'Predictor Mode and the Streak Lounge',
  },
  {
    n: 3,
    days: 30,
    votes: 500,
    title: 'Creator Power',
    reward: 'Extended feed boost on your questions (2h to 6h)',
  },
];

/**
 * Is a tier unlocked? Union of the server's stored array and a fresh compute
 * from streak/votes, matching the backend's own union logic — a stored empty
 * array is truthy, so trusting it alone would lock out users whose row
 * predates the vote-count path.
 */
export function isTierUnlocked(tier, streak) {
  const unlocked = streak?.unlocked_tiers || [];
  if (unlocked.includes(tier.n)) return true;
  if ((streak?.current_streak ?? 0) >= tier.days) return true;
  return (streak?.total_votes ?? 0) >= tier.votes;
}

/**
 * The highest tier the user currently holds; 0 when none.
 */
export function currentTierNumber(streak) {
  return TIERS.reduce((acc, tier) => (isTierUnlocked(tier, streak) ? tier.n : acc), 0);
}

/**
 * The next tier to chase, or null at max.
 */
export function nextTier(streak) {
  return TIERS.find((tier) => !isTierUnlocked(tier, streak)) || null;
}

/**
 * Progress toward a tier along whichever of the two unlock paths the user is
 * further along. Tiers unlock on days OR lifetime votes (see `isTierUnlocked`),
 * so measuring the days path alone told someone sitting at 120/500 votes with
 * a lapsed streak that they had zero progress and "30 days to tier 3" — the
 * bar contradicted the very rule that would unlock them.
 *
 * Returns the percentage to fill plus the remaining count and its unit, so the
 * caller's copy names the same path the bar is measuring.
 */
export function nextTierProgress(tier, streak) {
  if (!tier) return null;
  const currentStreak = streak?.current_streak ?? 0;
  const totalVotes = streak?.total_votes ?? 0;
  const daysPct = Math.min(100, (currentStreak / tier.days) * 100);
  const votesPct = Math.min(100, (totalVotes / tier.votes) * 100);
  const useVotes = votesPct > daysPct;

  return {
    percent: Math.max(daysPct, votesPct),
    remaining: useVotes
      ? Math.max(0, tier.votes - totalVotes)
      : Math.max(0, tier.days - currentStreak),
    unit: useVotes ? 'vote' : 'day',
  };
}

/**
 * Which visual state the streak pill renders.
 *
 * Written as early returns rather than a chained ternary: `.eslintrc` extends
 * airbnb, which sets `no-nested-ternary` to error.
 */
export function deriveStreakState(streak) {
  if (!streak) return 'active';
  if (streak.recovery_active) return 'recovery';
  const goal = streak.daily_vote_goal ?? 3;
  if ((streak.daily_vote_count ?? 0) >= goal) return 'safe';

  // GUARD — required. `last_vote_timestamp` is absent on the anonymous branch
  // and on the no-VoteStreak-row branch of GET /engagement/streak/. `new
  // Date(null)` is epoch 0, so hoursSince would be ~500,000 and a
  // freshly-registered user would render the flat YELLOW "about to break"
  // pill with no streak to break.
  if (!streak.last_vote_timestamp || (streak.current_streak ?? 0) === 0) return 'active';

  const hoursSince = (Date.now() - new Date(streak.last_vote_timestamp).getTime()) / 3.6e6;
  if (Number.isNaN(hoursSince)) return 'active';
  if (hoursSince >= 18) return 'at_risk';
  return 'active';
}

/**
 * Format a millisecond remainder as H:MM:SS. Replaces the hardcoded "within 24
 * hours" copy — the real deadline ships as `recovery_expires_at`.
 * Returns null when there is nothing meaningful to count down to, so callers
 * can decline to mount a ticker on the fallback response shapes.
 */
export function formatCountdown(expiresAt, now = Date.now()) {
  if (!expiresAt) return null;
  const target = new Date(expiresAt).getTime();
  if (Number.isNaN(target)) return null;
  const remaining = target - now;
  if (remaining <= 0) return '0:00:00';
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * The routine post-vote toast, or null for silence. The old inline version
 * toasted `n/goal` on EVERY vote — after the daily goal that meant "4/3",
 * "17/3"… forever. Rules now:
 *   - below the goal: progress nudge ("1/3 votes today for your streak")
 *   - exactly at the goal (the completing vote): one "locked in" line
 *   - beyond the goal: quiet, unless the user is within 10 lifetime votes of
 *     the next tier's vote-unlock — the only moment worth breaking silence.
 * Reward moments (shield/recovery/tier/broken) are handled upstream by
 * deriveRewardMoment and never reach this function's caller path.
 */
export const TIER_NUDGE_WINDOW = 10;

export function deriveStreakToast(streak) {
  if (!streak) return null;
  const goal = streak.daily_vote_goal ?? 3;
  const counted = streak.daily_vote_count ?? 0;
  if (counted < goal) return `${counted}/${goal} votes today for your streak`;
  if (counted === goal) return `${goal}/${goal} votes today — streak locked in`;

  const tier = nextTier(streak);
  if (!tier) return null;
  const remaining = tier.votes - (streak.total_votes ?? 0);
  if (remaining >= 1 && remaining <= TIER_NUDGE_WINDOW) {
    const noun = remaining === 1 ? 'vote' : 'votes';
    return `${remaining} ${noun} to Tier ${tier.n}: ${tier.title}`;
  }
  return null;
}
